import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HeadersService } from '../header.service';
import {
  AlterarClassificacaoPrestacaoContaRequest,
  AprovarPrestacaoContaEmLoteRequest,
  AprovarPrestacaoContaEmLoteResponse,
  AprovarPrestacaoContaRequest,
  ConsultarPrestacoesContaFiltro,
  MultiPrestacaoContaDetalheDTO,
  MultiPrestacaoContaDetalheResponse,
  MultiPrestacaoContaListaResponse,
  MultiPrestacaoContaPendenteDTO,
  RecusarDefinitivamentePrestacaoContaRequest,
  SolicitarCorrecaoPrestacaoContaRequest,
  StatusListReponse
} from 'src/models/interfaces/cartao/prestacao-conta-multi.interface';
import { BasicResponse } from 'src/models/interfaces/basic-response.interface';
import { LOADING_INSTANCE } from 'src/app/interceptor/network.interceptor';
import {
  AprovacaoPrestacoesActionType,
  AprovacaoPrestacoesLoadingKeys
} from 'src/models/keys/aprovacao-prestacoes-multi/aprovacao-prestacoes-multi.keys';

@Injectable({
  providedIn: 'root'
})
export class PrestacaoContaMultiService {
  private readonly url = `${environment.apiUrl}/api/v1/multi/prestacoes-conta`;
  private readonly defaultLoadingKey = new HttpContext().set(LOADING_INSTANCE, AprovacaoPrestacoesLoadingKeys.Default);
  private readonly detailLoadingKey = new HttpContext().set(
    LOADING_INSTANCE,
    AprovacaoPrestacoesLoadingKeys.DetailsContent
  );
  private readonly actionLoadingKey = (type: AprovacaoPrestacoesActionType) =>
    new HttpContext().set(LOADING_INSTANCE, AprovacaoPrestacoesLoadingKeys.Action(type));
  private readonly imageLoadingKey = new HttpContext().set(LOADING_INSTANCE, AprovacaoPrestacoesLoadingKeys.Image);
  private readonly classificacaoLoadingKey = new HttpContext().set(
    LOADING_INSTANCE,
    AprovacaoPrestacoesLoadingKeys.Classificacao
  );
  constructor(
    private readonly http: HttpClient,
    private headersService: HeadersService
  ) {
    this.headersService.includeObject({ fileName: 'prestacao-conta-multi.service.ts', url: this.url });
  }

  public listarPrestacoes(filtro: ConsultarPrestacoesContaFiltro): Observable<MultiPrestacaoContaPendenteDTO[]> {
    let params = new HttpParams().set('dataInicial', filtro.dataInicial).set('dataFinal', filtro.dataFinal);

    if (filtro.status) {
      params = params.set('status', filtro.status);
    }
    if (filtro.idCentroCusto != null) {
      params = params.set('idCentroCusto', String(filtro.idCentroCusto));
    }
    if (filtro.idProjeto != null) {
      params = params.set('idProjeto', String(filtro.idProjeto));
    }
    if (filtro.funcionario?.trim()) {
      params = params.set('funcionario', filtro.funcionario.trim());
    }
    if (filtro.somenteComComprovante?.trim()) {
      params = params.set('somenteComComprovante', filtro.somenteComComprovante.trim());
    }

    if (filtro.minhasPendencias) {
      params = params.set('minhasPendencias', filtro.minhasPendencias);
    }

    return this.http
      .get<MultiPrestacaoContaListaResponse>(this.url, { params, context: this.defaultLoadingKey })
      .pipe(map((response) => response?.prestacoes ?? []));
  }

  public consultarDetalhe(idPrestacaoConta: number): Observable<MultiPrestacaoContaDetalheDTO> {
    return this.http
      .get<MultiPrestacaoContaDetalheResponse>(`${this.url}/${idPrestacaoConta}`, { context: this.detailLoadingKey })
      .pipe(map((response) => response.detalhe));
  }

  public aprovar(idPrestacaoConta: number, request: AprovarPrestacaoContaRequest): Observable<BasicResponse> {
    return this.http.post<BasicResponse>(`${this.url}/${idPrestacaoConta}/aprovar`, request, {
      context: this.actionLoadingKey('aprovar')
    });
  }

  public aprovarLote(request: AprovarPrestacaoContaEmLoteRequest): Observable<AprovarPrestacaoContaEmLoteResponse> {
    return this.http.post<AprovarPrestacaoContaEmLoteResponse>(`${this.url}/aprovar-lote`, request, {
      context: this.actionLoadingKey('aprovar')
    });
  }

  public solicitarCorrecao(
    idPrestacaoConta: number,
    request: SolicitarCorrecaoPrestacaoContaRequest
  ): Observable<BasicResponse> {
    return this.http.post<BasicResponse>(`${this.url}/${idPrestacaoConta}/solicitar-correcao`, request, {
      context: this.actionLoadingKey('corrigir')
    });
  }

  public recusarDefinitivamente(
    idPrestacaoConta: number,
    request: RecusarDefinitivamentePrestacaoContaRequest
  ): Observable<BasicResponse> {
    return this.http.post<BasicResponse>(`${this.url}/${idPrestacaoConta}/recusar-definitivamente`, request, {
      context: this.actionLoadingKey('recusar')
    });
  }

  public alterarClassificacao(
    idPrestacaoConta: number,
    request: AlterarClassificacaoPrestacaoContaRequest
  ): Observable<BasicResponse> {
    return this.http.put<BasicResponse>(`${this.url}/${idPrestacaoConta}/classificacao`, request, {
      context: this.classificacaoLoadingKey
    });
  }

  public listarStatus(): Observable<StatusListReponse> {
    return this.http.get<StatusListReponse>(`${this.url}/status`, { context: this.defaultLoadingKey });
  }

  public consultarImagem(idPrestacaoConta: number): Observable<Blob> {
    return this.http.get(`${this.url}/${idPrestacaoConta}/comprovante/imagem`, {
      context: this.imageLoadingKey,
      responseType: 'blob'
    });
  }
}
